import { ApplicationConfig, ErrorHandler, LOCALE_ID, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideNgxMask } from 'ngx-mask';
import { authInterceptor } from './shared/interceptors/auth.interceptor';
import { apiEnvironmentInterceptor } from './shared/interceptors/api-environment.interceptor';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { SlackErrorHandler } from './shared/services/slack-error-handler';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '../environments/environment';

/** Roda na máquina do dev (`ng serve`), onde o service worker não é emitido. */
function isLocalhost(): boolean {
  if (typeof location === 'undefined') return false;
  return ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);
}

// Função para inicializar as traduções antes da aplicação iniciar
// Seguindo as melhores práticas: usar o TranslateHttpLoader nativo e apenas configurar o idioma padrão
export function appInitializerFactory(translate: TranslateService) {
  return () => {
    // Configurar idiomas disponíveis
    translate.addLangs(['pt', 'en', 'es']);
    translate.setDefaultLang('pt');
    
    // Usar o idioma padrão - o TranslateHttpLoader carregará automaticamente as traduções
    return translate.use('pt').toPromise();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    BsDatepickerModule.forRoot().providers!,
    provideAnimations(),
    provideHttpClient(
      // Ordem importa: o `apiEnvironmentInterceptor` reescreve a base URL antes
      // do `authInterceptor` anexar o Bearer, garantindo que o token seja
      // aplicado à URL definitiva.
      withInterceptors([apiEnvironmentInterceptor, authInterceptor])
    ),
    provideNgxMask(),
    // IMPORTANTE: provideTranslateHttpLoader deve vir ANTES de provideTranslateService
    provideTranslateHttpLoader({
      prefix: '/assets/i18n/',
      suffix: '.json'
    }),
    provideTranslateService({
      fallbackLang: 'pt',
      defaultLanguage: 'pt',
      useDefaultLang: true
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService],
      multi: true
    },
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: ErrorHandler, useClass: SlackErrorHandler },
    // PWA. `registerWhenStable` espera a aplicação ficar ociosa antes de registrar, para o
    // download do service worker não competir com as primeiras requisições da tela.
    //
    // O guard de localhost é necessário além da flag de ambiente: `ng serve` usa a configuração
    // `development` (que tem a flag ligada, porque o ambiente de dev publicado precisa do PWA),
    // mas o dev server **não emite** `ngsw-worker.js` — sem o guard, cada carga local tentaria
    // registrar um arquivo inexistente e sujaria o console com 404.
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.serviceWorker && !isLocalhost(),
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],

};
